// The map controls: northern lights, compass, overview, the amount of trees, and the map-data credit (a licence
// duty, not a control: its button is always there).
// Controls: aurora.toggle, aurora.panel, compass, home, locate, wayfind, trees. They call the map's own functions
// (api.setAurora, the tree layer), which report the visitor's choice to the runtime. No zoom, quality, clouds, map
// layers or snow-on-trees button in any style (owner, 2026-10-28: settings/controls.js NO_BUTTON); the wheel, a pinch
// and the keys zoom.
import { html, safeUrl } from '../dom.js';
import { auroraHost, auroraSync, isDark } from '../auroraPanel.js';
import { icon } from './icons.js';
import { fit, popup } from './popup.js';
import { creditItems } from './privacy.js'; // the credits, should the info box's module not come (lazy/infobox.js)

/**
 * WS-91 part 2: the wayfinding spot, where "Where to?" and the route bar of lazy/locate.js stand: the top middle of
 * the map, under whatever the page puts there (a top bar, a search capsule, a button), never on a panel or a card.
 * Looks down from the top for the first band `w` x `h` px that nothing of the interface covers and writes its top to
 * `--wf-top`; none in the upper part of the screen: `wf-full` on the body (the button waits for room). Returns the top
 * (px) or null.
 */
export function fitSpot(w = 280, h = 46) {
  const chrome = document.getElementById('chrome'), W = innerWidth, H = innerHeight, body = document.body;
  if (!chrome || !W || !H || body.classList.contains('ph')) return null; // VIS-230: a phone's "Where to?" and route stand at the bottom
  const half = Math.min(w, W - 24) / 2, xs = [-1, -0.5, 0, 0.5, 1].map((k) => W / 2 + k * (half - 2));
  const over = (x, y) => document.elementsFromPoint(x, y).find((el) => el !== chrome && (chrome.contains(el) || el.id === 'feature-card') && !el.closest('.wf'));
  let y = 20, top = null; // the height of the top bars of every style (and of the route bar before WS-91 part 2)
  for (let n = 0; n < 16 && y + h < H * 0.4; n++) {
    let low = 0;
    for (const yy of [y + 1, y + h / 2, y + h]) for (const x of xs) { const el = over(x, yy); if (el) low = Math.max(low, el.getBoundingClientRect().bottom); }
    if (!low) { top = Math.round(y); break; }
    y = low + 8;
  }
  if (top !== null && document.documentElement.style.getPropertyValue('--wf-top') !== `${top}px`) document.documentElement.style.setProperty('--wf-top', `${top}px`);
  if (body.classList.contains('wf-full') !== (top === null)) body.classList.toggle('wf-full', top === null); // only a change: the body's classes are watched
  return top;
}

export function columnParts(c) {
  const { api, T, tx, i18n } = c; // (tx: the resort's name in the info box)
  const $ = (sel) => document.querySelector(sel);
  const TR = api.trees;
  const percent = (v) => T('percent', { n: i18n.num(Math.round(v * 100)) });
  // WS-45 map-data credit. resort.json attribution: [{ text, href, showAtStart }]: the data licences (MML CC BY 4.0,
  // OSM ODbL, ...) require a credit on every delivered map, whatever the preset shows. An (i) button in the map
  // controls opens the list. Entries with showAtStart (OSM asks for a credit that is seen first and may then fold
  // away) are shown alone, as a one-line chip beside the controls, once the controls are in; the chip folds after 5 s
  // or on the first touch, wheel or key, and the full list stays behind (i). CC BY credits (MML) may sit behind a link.
  const credits = (Array.isArray(api.resort.attribution) ? api.resort.attribution : []).filter((x) => x && x.text);
  let compass = null, syncTrees = null, greeted = false;
  // VIS-231: "Save for offline", a row of the same panel (lazy/save.js, fetched when the panel opens): only on a page
  // the host offers whole (boot field `pack`), in a browser with service workers, and not on a placement that keeps
  // its copy by itself (boot field `offline`: js/boot.js). The (i) button is there when it has credits or this row.
  const meta = (n) => globalThis.document?.querySelector(`meta[name="mapmystay-${n}"]`)?.content || '';
  const savable = () => !!meta('pack') && 'serviceWorker' in (globalThis.navigator || {}) && !meta('offline');

  // WS-45: the start credit chip (showAtStart entries only), beside the map controls: above them when they are a row
  // (desktop), left of the column beside its (i) button when they are a column. It
  // folds after 5 s or on the first touch, wheel or key outside it (OSMF attribution guideline: may fade after five
  // seconds or on map interaction, if the licence stays findable: the (i) list).
  // VIS-128: `id` given = a short note of the map in the same place (the northern lights in daylight), said as a status
  function startChip(markup, id) {
    const chip = document.createElement('div');
    chip.id = id || 'credit-chip'; chip.className = 'credit-chip'; chip.setAttribute('role', id ? 'status' : 'note'); if (!id) chip.setAttribute('aria-label', T('credit.label'));
    chip.innerHTML = html`${markup}`;
    document.body.appendChild(chip);
    // a column: left of it, level with its own button (the (i) sits low in the column, by the bottom-right corner).
    // Measured every frame while it shows: the controls still slide in and may be drawn again (a preset, a card), and
    // a spot taken once was left wherever they were then
    const place = () => {
      const btn = $(id ? '#aurora-btn' : '#credit-btn'), bar = btn?.closest('.controls, .ph-rail'); // (VIS-230: a phone's column)
      if (!btn || !bar || !btn.offsetWidth) return; // the host hides the button: the chip keeps its CSS corner
      const b = bar.getBoundingClientRect(), i = btn.getBoundingClientRect();
      if (b.width >= b.height) { chip.style.right = `${innerWidth - b.right}px`; chip.style.bottom = `${innerHeight - b.top + 8}px`; }
      else { chip.style.right = `${innerWidth - b.left + 8}px`; chip.style.bottom = `${innerHeight - i.bottom + (i.height - chip.offsetHeight) / 2}px`; }
    };
    let follow = 0;
    const track = () => { place(); follow = requestAnimationFrame(track); };
    track();
    requestAnimationFrame(() => chip.classList.add('on'));
    const fold = (e) => {
      if (e && (chip.contains(e.target) || e.key === 'Tab' || e.key === 'Shift')) return; // Tab may walk to its link
      clearTimeout(timer);
      cancelAnimationFrame(follow);
      for (const [ev, f] of offs) removeEventListener(ev, f, true);
      chip.classList.remove('on');
      setTimeout(() => chip.remove(), 400);
    };
    const timer = setTimeout(fold, 5000);
    const offs = [['pointerdown', fold], ['wheel', fold], ['keydown', fold]];
    for (const [ev, f] of offs) addEventListener(ev, f, true);
  }

  const syncAurora = () => { $('#aurora-btn')?.classList.toggle('on', !!api.getState().auroraOn); auroraSync(); };

  const own = (v) => (typeof v === 'string' ? v.trim() : v && typeof v === 'object' ? String(v[i18n.lang] ?? v[i18n.lang.split('-')[0]] ?? '').trim() : '') || null; // WS-91
  const parts = {
    'aurora.toggle': {
      html: () => html`<button class="round-btn toggle on" id="aurora-btn" title="${T('aurora.button')}">${icon('aurora')}</button>`,
      wire(root) {
        const b = root.querySelector('#aurora-btn');
        // VIS-128: switched on while the sky is light, it says why nothing shows yet (no silent gold button)
        b.onclick = () => { const on = !b.classList.contains('on'); b.classList.toggle('on', on); api.setAurora(on); if (on && !isDark(api.getState())) startChip(T('aurora.needsDark'), 'aurora-note'); };
      },
    },
    'aurora.panel': {}, // no element of its own: the popover of auroraPanel.js hangs on the button (wire below)
    compass: {
      html: () => html`<button class="round-btn" id="compass" title="${T('compass.title')}" aria-label="${T('compass.label')}"><span class="needle"></span><i>${T('dir.n')}</i></button>`, // VIS-128: not read out as "N"
      wire(root) { root.querySelector('#compass').onclick = api.northUp; },
    },
    home: {
      html: () => html`<button class="round-btn" id="home-btn" title="${T('home')}">${icon('home')}</button>`,
      wire(root) { root.querySelector('#home-btn').onclick = () => { api.select(null); api.home(); }; },
    },
    // WS-18: the amount of trees. Only when the resort uses the tree models. Snow on them is the owner's setting
    // (owner, 2026-10-28: no guest control).
    trees: {
      can: () => !!TR,
      html: () => html`<button class="round-btn" id="trees-btn" title="${T('trees')}" aria-haspopup="dialog" aria-expanded="false">${icon('tree')}</button><!-- WS-18 -->`,
      wire(root) {
        const p = popup('trees-pop', root.querySelector('#trees-btn'), 'trees-pop', T('trees')), pop = p.pop;
        pop.innerHTML = html`
          <div class="tp-head"><span class="eyebrow">${T('trees')}</span></div>
          <label class="tp-row"><span>${T('trees.amount')} <b id="tp-amt"></b></span>
            <input id="tp-density" type="range" min="0" max="100" step="5" aria-label="${T('trees.amount.label')}"></label>`;
        const q = (sel) => pop.querySelector(sel);
        const sync = () => { const s = TR.settings(); q('#tp-density').value = Math.round(s.density * 100); q('#tp-amt').textContent = percent(s.density); };
        q('#tp-density').addEventListener('input', (e) => { TR.setDensity(Number(e.target.value) / 100); sync(); });
        p.onOpen = sync;
        sync();
        syncTrees = sync;
      },
    },
    // WS-91: "Show where I am" (lazy/locate.js, fetched on the first press): the guest's position, followed as they
    // walk. Where the preset shows it, and on every wayfinding page (a scanned QR code); its state is lazy/locate.js's
    locate: {
      can: () => !!api.locate?.can(),
      html: () => html`<button class="round-btn" id="locate-btn" data-control="locate" title="${T('locate')}" aria-pressed="false">${icon('locate')}</button>`,
      wire: (root) => { const b = root?.querySelector('#locate-btn'); if (b) api.locate.wire(b); },
    },
    // WS-91 part 2: "Where to?" (lazy/whereto.js, fetched on the first press): one small button at the wayfinding spot
    // (the top middle of the map: fitSpot below), in a region of its own (page.js `wayfind`), not in the column.
    // Its words are the preset's (wayfind.button, live: main.js) in the visitor's language, else the engine's in that
    // language (never the client's words of another language: lazy/wayfind.js ownWords, the same rule).
    wayfind: {
      can: () => !!api.wayfind?.can(),
      html: (w = own(c.v('wayfind.button')) || T('wayfind')) => (c.phone // VIS-230: a phone's pin, at the top of the column (phone.js)
        ? html`<button class="ph-find ph-pin" id="wayfind-btn" data-control="wayfind" aria-haspopup="dialog" aria-expanded="false" aria-label="${w}" title="${w}">${icon('pin')}</button>`
        : html`<button class="pill-btn shell-btn st-glass wf-pill" id="wayfind-btn" data-control="wayfind" aria-haspopup="dialog" aria-expanded="false">${icon('search')}<span>${w}</span></button>`),
      wire: (root) => root?.querySelectorAll('[data-control="wayfind"]').forEach((b) => api.wayfind.wire(b)),
    },
  };

  const column = {
    slot: 'controls',
    skip: ['wayfind'], // WS-91 part 2: drawn in its own region (page.js `wayfind`)
    html(kids) {
      const vis = kids.filter((k) => k.html);
      if (credits.length || savable()) vis.push({ html: html`<button class="round-btn" id="credit-btn" title="${T('credit.label')}" aria-haspopup="dialog" aria-expanded="false">${icon('info')}</button><!-- WS-45 -->` });
      return vis.length ? html`<div class="controls">${vis.map((k, i) => html`${k.html}${k.id === 'aurora.toggle' && i < vis.length - 1 ? html`<span class="sep"></span>` : ''}`)}</div>` : '';
    },
    wire(root) {
      compass = root?.querySelector('#compass') || null;
      if (!c.has('trees')) syncTrees = null;
      auroraHost(c.has('aurora.panel') ? root?.querySelector('#aurora-btn') : null, c); // a phone's Northern lights row opens the same panel beside the column (phone.js POPS); without the panel the row is a switch
      const info = root?.querySelector('#credit-btn');
      if (!info) return;
      // The info box, one in every style (owner's pick I2, 2026-10-29): lazy/infobox.js fills it when it first opens
      // (the wordmark and the arrow to mapmystay.co, who made the map, the tabs Map data, Privacy and Offline). Should
      // that module not come, the box still lists the credits (a licence duty).
      const p = popup('credit-pop', info, 'trees-pop credit-pop', T('credit.label'));
      const link = (x) => (safeUrl(x.href) ? html`<a href="${safeUrl(x.href)}" target="_blank" rel="noopener">${x.text}</a>` : html`<span>${x.text}</span>`);
      p.pop.tabIndex = -1; // opened, the focus goes to the box itself (no ring on its first link: the old fault); Tab walks on into it
      p.onOpen = () => {
        p.onOpen = null;
        import('../lazy/infobox.js').then((m) => m.fill(p.pop, { credits, savable: savable(), T, tx, lang: i18n.lang }), (e) => { console.warn(e); p.pop.innerHTML = html`<ul>${creditItems(credits)}</ul>`; })
          .then(() => { if (!p.pop.hidden) fit(p.pop); });
      };
      const open = info.onclick; // popup.js: from the keyboard the focus would go to the first link
      info.onclick = (e) => { open(e); if (!p.pop.hidden) p.pop.focus({ preventScroll: true }); };
      const first = credits.filter((x) => x.showAtStart);
      if (greeted || !first.length) return;
      greeted = true;
      const wait = setInterval(() => {
        if (!document.body.classList.contains('ready')) return;
        clearInterval(wait);
        // the controls slide in after the scene (style.css chrome-in): the 5 s start when they, and the chip, are seen
        const bar = info.closest('.controls');
        let shown = false;
        const go = () => { if (!shown) { shown = true; startChip(html`${first.map((x, i) => html`${i ? ' · ' : ''}${link(x)}`)}`); } };
        bar?.addEventListener('animationend', go, { once: true });
        setTimeout(go, 3000);
      }, 250);
    },
  };

  // WS-91 part 2: the region of "Where to?": the button alone, at the wayfinding spot, which follows what the page
  // shows at the top (a resize, a panel or a card that opens: the body's classes, the panels' own movements)
  let watching = false, fitTimer = 0;
  const fitSoon = () => { clearTimeout(fitTimer); fitTimer = setTimeout(fitSpot, 90); };
  const wayfind = {
    slot: 'controls',
    only: ['wayfind'],
    key: () => JSON.stringify([c.v('wayfind.button') ?? null, c.v('wayfind.prompt') ?? null]), // the preset's words changed: drawn again (ui.js build)
    html: (kids) => (kids.some((k) => k.html) ? html`<div class="wf" id="wayfind">${kids.map((k) => k.html)}</div>` : ''),
    wire(root) {
      if (!root || c.phone) return; // VIS-230: a phone's pin stands in the column, not at the wayfinding spot
      fitSoon(); setTimeout(fitSpot, 1400); // again once the controls have slid in (style.css chrome-in)
      if (watching) return;
      watching = true;
      addEventListener('resize', fitSoon);
      new MutationObserver(fitSoon).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.getElementById('chrome')?.addEventListener('transitionend', (e) => { if (e.propertyName === 'transform' || e.propertyName === 'opacity') fitSoon(); });
      document.getElementById('chrome')?.addEventListener('animationend', fitSoon);
    },
  };

  return {
    parts, column, wayfind, syncAurora,
    start() {},
    sync() { syncAurora(); syncTrees?.(); },
    /** The compass needle follows the camera. */
    turn(az) { compass?.style.setProperty('--rot', `${az}rad`); },
  };
}
